/**
 * PageTree Surface Component
 * Hierarchical page navigation
 */

import React, { useState } from 'react';
import { Stack, Text, Group, ActionIcon, Badge, ScrollArea, Box, UnstyledButton } from '@mantine/core';
import { IconChevronRight, IconChevronDown, IconPlus, IconFile, IconFolder } from '@tabler/icons-react';

interface PageTreeProps {
  phase?: number;
}

interface PageNode {
  id: string;
  title: string;
  children?: PageNode[];
}

const defaultPages: PageNode[] = [
  {
    id: '1',
    title: 'Getting Started',
    children: [
      { id: '1-1', title: 'Installation' },
      { id: '1-2', title: 'Configuration' }
    ]
  },
  {
    id: '2',
    title: 'Features',
    children: [
      { id: '2-1', title: 'Workspace' },
      { id: '2-2', title: 'Editor' },
      { id: '2-3', title: 'Database' }
    ]
  },
  { id: '3', title: 'Documentation' }
];

const PageTree: React.FC<PageTreeProps> = ({ phase = 1 }) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set(['1', '2']));

  const toggleExpand = (id: string) => {
    const newExpanded = new Set(expanded);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpanded(newExpanded);
  };

  const renderPage = (page: PageNode, depth: number = 0) => {
    const hasChildren = page.children && page.children.length > 0;
    const isExpanded = expanded.has(page.id);

    return (
      <Box key={page.id}>
        <UnstyledButton
          onClick={() => hasChildren && toggleExpand(page.id)}
          px="sm"
          py="xs"
          style={{
            width: '100%',
            borderRadius: 4,
            marginLeft: depth * 16,
            '&:hover': { backgroundColor: 'var(--mantine-color-gray-1)' }
          }}
        >
          <Group gap="xs">
            {hasChildren && (
              <ActionIcon variant="subtle" size="xs" onClick={(e) => { e.stopPropagation(); toggleExpand(page.id); }}>
                {isExpanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
              </ActionIcon>
            )}
            {!hasChildren && <Box w={20} />}
            {hasChildren ? <IconFolder size={16} /> : <IconFile size={16} />}
            <Text size="sm">{page.title}</Text>
          </Group>
        </UnstyledButton>
        {hasChildren && isExpanded && (
          <Box>
            {page.children!.map(child => renderPage(child, depth + 1))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Stack gap="md" p="sm">
      <Group justify="space-between" align="center">
        <Text fw={600} size="sm">Pages</Text>
        <ActionIcon variant="subtle" size="sm">
          <IconPlus size={16} />
        </ActionIcon>
      </Group>

      <Badge size="sm" variant="light" mb="xs">Phase {phase}</Badge>

      <ScrollArea h={400}>
        <Stack gap={2}>
          {defaultPages.map(page => renderPage(page))}
        </Stack>
      </ScrollArea>
    </Stack>
  );
};

export default PageTree;